// useSaleOffer — the SINGLE source of truth for the on-sale price display (F-163).
//
// It asks the server (the existing public /order/validate-promo endpoint) whether the discount code is live
// RIGHT NOW and at what price. That endpoint is already cap-aware: it returns valid:false the moment the code
// is inactive OR has hit its redemption cap. So this hook is HONEST by construction — if the discount can't be
// honored, saleActive is false and every surface reverts to the plain anchor price. No new price source is
// created; the server still bakes the real discount at checkout.
//
// COPY RULES (founder-ratified, F-163-GO): no "launch" wording anywhere, no urgency wording, and NO visibility
// into remaining/bought counts. The code name is an internal detail; the only user-facing strings this module
// owns are the tag ("Save $30") and the applied-state label ("Discount applied · TALE30").
import React from 'react';
import { ORDER_API_BASE } from '../constants/config';

export const SALE_OFFER = {
  code: 'TALE30',
  anchorCents: 8999,           // the regular price; the discount is validated server-side, never trusted here
  tag: 'Save $30',             // user-facing — NO "launch", NO urgency, NO counts
  appliedLabel: 'Discount applied · TALE30',
};

export const fmtUsd = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;

const inactive = () => ({
  loading: false, saleActive: false,
  anchorCents: SALE_OFFER.anchorCents, saleCents: SALE_OFFER.anchorCents,
  discountCents: 0, code: SALE_OFFER.code,
});

export function useSaleOffer() {
  const [state, setState] = React.useState({ ...inactive(), loading: true });

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await fetch(`${ORDER_API_BASE}/order/validate-promo`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ promoCode: SALE_OFFER.code }),
        });
        const data = await resp.json();
        if (!alive) return;
        if (data && data.valid && typeof data.finalPriceCents === 'number' && data.finalPriceCents < SALE_OFFER.anchorCents) {
          setState({
            loading: false, saleActive: true,
            anchorCents: SALE_OFFER.anchorCents,
            saleCents: data.finalPriceCents,
            discountCents: data.discountCents || (SALE_OFFER.anchorCents - data.finalPriceCents),
            code: SALE_OFFER.code,
          });
        } else {
          setState(inactive()); // capped, inactive, or no discount → honest revert to the anchor price
        }
      } catch {
        if (alive) setState(inactive()); // never block the page on analytics/price-preview failure
      }
    })();
    return () => { alive = false; };
  }, []);

  return state;
}
